import React, { useEffect, useState } from "react";
import { getTestScoreSummary } from "../pages/services/performanceService";
import { DIFFICULTY_ORDER, difficultyColor } from "./utils/difficulty";
import "../styles/TestScoreSummary.css";

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
  });
};


// Durations are stored as loose strings ("45m", "1h", "90"). Render them as
// hours and minutes regardless of which form was saved.
const fmtDuration = (raw) => {
  if (!raw) return "—";
  const str = String(raw).trim();
  let minutes = null;

  const h = str.match(/(\d+(?:\.\d+)?)\s*h/i);
  const m = str.match(/(\d+(?:\.\d+)?)\s*m/i);
  if (h || m) minutes = (h ? parseFloat(h[1]) * 60 : 0) + (m ? parseFloat(m[1]) : 0);
  else if (/^\d+(\.\d+)?$/.test(str)) minutes = parseFloat(str);   // bare number = minutes

  if (minutes === null || isNaN(minutes)) return str;                // unparseable: show as saved
  const hrs = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (!hrs) return `${mins} min`;
  return mins ? `${hrs} hr ${mins} min` : `${hrs} hr`;
};

// Seconds -> "1 hr 30 min". Used for the time a student actually took, as
// opposed to the paper's allowance which arrives as a string.
const fmtSeconds = (secs) => {
  if (secs == null) return "—";
  const hrs = Math.floor(secs / 3600);
  const mins = Math.round((secs % 3600) / 60);
  if (!hrs) return `${mins} min`;
  return mins ? `${hrs} hr ${mins} min` : `${hrs} hr`;
};

const TestScoreSummary = ({ testId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!testId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getTestScoreSummary(testId)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [testId]);

  if (loading) return <div className="tss-state">Loading test metrics…</div>;
  if (error) return <div className="tss-state tss-state-error">{error}</div>;
  if (!data) return null;

  // Only levels the paper actually contains, in hardest-last order.
  const levels = DIFFICULTY_ORDER.filter((l) => data.difficultyCounts[l]);

  return (
    <div className="tss-card">
      <h3 className="tss-heading">Test Score Summary</h3>

      <dl className="tss-meta">
        <div><dt>Test title</dt><dd>{data.testTitle}</dd></div>
        <div>
          <dt>Test modules</dt>
          <dd>{data.modules.length ? data.modules.join(", ") : <span className="tss-muted">—</span>}</dd>
        </div>
        <div>
          <dt>Total questions</dt>
          <dd>
            <strong>{data.totalQuestions}</strong>
            {levels.length > 0 && (
              <span className="tss-breakdown">
                {" ( "}
                {levels.map((level, i) => (
                  <React.Fragment key={level}>
                    {i > 0 && ", "}
                    {/* Bare counts, coloured by difficulty. The word only
                        appears on hover, so the line stays scannable. */}
                    <span
                      className="tss-count"
                      style={{ color: difficultyColor(level) }}
                      title={`${data.difficultyCounts[level]} ${level} question${data.difficultyCounts[level] === 1 ? "" : "s"}`}
                    >
                      {data.difficultyCounts[level]}
                    </span>
                  </React.Fragment>
                ))}
                {" )"}
              </span>
            )}
          </dd>
        </div>
        {data.schedule && (
          <>
            <div><dt>Test duration</dt><dd>{fmtDuration(data.schedule.testDuration)}</dd></div>
            <div><dt>Start time</dt><dd>{fmtWhen(data.schedule.startedAt)}</dd></div>
            <div><dt>End time</dt><dd>{fmtWhen(data.schedule.submittedAt)}</dd></div>
            {data.schedule.isAnalysisRequired ? (
              <>
                <div><dt>Analysis duration</dt><dd>{fmtDuration(data.schedule.analysisDuration)}</dd></div>
                <div><dt>Analysis start time</dt><dd>{fmtWhen(data.schedule.analysisStartedAt)}</dd></div>
                <div><dt>Analysis end time</dt><dd>{fmtWhen(data.schedule.analysisCompletedAt)}</dd></div>
              </>
            ) : (
              <div><dt>Analysis</dt><dd className="tss-muted">Not required for this test</dd></div>
            )}
          </>
        )}
      </dl>

      {data.rows.length === 0 ? (
        <p className="tss-empty">No student has attempted this test yet.</p>
      ) : (
        <div className="tss-table-wrap">
          <table className="tss-table">
            <thead>
              <tr>
                <th>Student</th>
                <th className="tss-num">Accuracy %</th>
                <th className="tss-num">Total Qs</th>
                <th className="tss-num">Correct Qs</th>
                <th className="tss-num">Incorrect Qs</th>
                <th className="tss-num">Unattempted Qs</th>
                <th>Started</th>
                <th>Submitted</th>
                <th>Test duration</th>
                <th>Analysis started</th>
                <th>Analysis completed</th>
                <th>Analysis duration</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.studentId}>
                  <td>{r.studentName}</td>
                  <td className="tss-num tss-accuracy">{r.accuracy}%</td>
                  <td className="tss-num">{r.total}</td>
                  <td className="tss-num tss-correct">{r.correct}</td>
                  <td className="tss-num tss-incorrect">{r.incorrect}</td>
                  <td className="tss-num tss-unattempted">{r.unattempted}</td>
                  <td>{fmtWhen(r.startedAt)}</td>
                  <td>{fmtWhen(r.submittedAt)}</td>
                  {/* The time actually taken, not the paper's allowance. */}
                  <td>{fmtSeconds(r.takenSeconds)}</td>
                  <td>
                    {fmtWhen(r.analysisStartedAt)}
                    {r.analysisStartedApprox && r.analysisStartedAt && (
                      <span className="tss-approx" title="Inferred from the first analysis view after this submission — the app does not tag analysis events with a test id.">≈</span>
                    )}
                  </td>
                  <td className="tss-muted" title="The student app emits no analysis-completed event yet, so this cannot be reported.">
                    {fmtWhen(r.analysisCompletedAt)}
                  </td>
                  <td className="tss-muted">{fmtSeconds(r.analysisSeconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TestScoreSummary;
